/**
 * Given a path and a subdomain, generates a relative or absolute URL to the correct subdomain.
 * @param {string} path - relative url path, including leading /
 * @param {string} subdomain - subdomain to link to, without any target process subdomain
 * @param {string} [hostname] - the current host. Required for SSR
 * @param {string} [port] - the current port, if required. Should be automatically detected.
 * @returns {string} a relative path to the same subdomain or an absolute path to a different one
 */
export default function getCrossDomainUrl({
  path,
  subdomain = 'player',
  hostname,
  port,
}) {
  if (typeof window !== 'undefined') {
    /* eslint-disable no-param-reassign */
    if (typeof hostname === 'undefined') ({ hostname } = window.location)
    if (typeof port === 'undefined') ({ port } = window.location)
  } else if (process?.env?.STV_ENV === 'development' || process?.env?.STV_ENV === 'testing') {
    ({ port } = process.env)
  }
  /* eslint-enable no-param-reassign */

  const parts = hostname.split('.')

  // this should be a string of 5 numbers or null
  const tpSubdomain = parts[0].match(/[0-9]{5,}/g)

  // get the subdomain without the tp prefix, if present
  const currentSubdomain = tpSubdomain
    ? parts.slice(1, -2).join('.')
    : parts.slice(0, -2).join('.')

  // If we're linking to the subdomain we're currently on,
  // bail out early and just use a relative link
  const sameDomain = (subdomain === currentSubdomain)
  if (sameDomain) return path

  // last two parts, e.g. ['stv', 'tv']
  const domain = parts.slice(-2).join('.')

  // get port into the right format if necessary
  const _port = (port === '' || typeof port === 'undefined') ? '' : `:${port}`

  if (tpSubdomain) {
    return `//${tpSubdomain}.${subdomain}.${domain}${_port}${path}`
  }

  return `//${subdomain}.${domain}${_port}${path}`
}
